import { FormActions } from "../../components/common/FormActions";
import { API_MERCHANT_USERS, API_USERS } from "../../utils/apis";
import {
  beautify,
  createEditAction,
  createViewAction,
  formatDate,
  getForm,
  getRegister,
  itemExists,
  useGetUserPermissions,
} from "../../utils/helpers";
import { URL_MERCHANT_USERS } from "../../utils/urls";
import { StatusTags } from "../../components/common/registerUtils";
import {
  ACCOUNTANT,
  ADMIN,
  AGENT,
  CAN_ADMIN,
  STAFF,
} from "../../utils/constants";
import { useAuth } from "../../context/AuthContext";
import { IFormSection, TFormHookParams } from "../../types/common";
import { VALIDATION_PRESETS } from "../../utils/formUtils";
import useGet from "../common/useGet";
import { useEffect, useRef, useState } from "react";

const url = URL_MERCHANT_USERS;

// REGISTER
export const useMerchantUsers = () => {
  const { user } = useAuth();
  const role = user?.role?.name;
  const tableColumns = () => [
    {
      id: "parent_merchant",
      header: "Parent Merchant",
      filter: {},
    },
    {
      id: "first_name",
      header: "First Name",
      filter: {},
    },
    {
      id: "last_name",
      header: "Last Name",
      filter: {},
    },
    {
      id: "email",
      header: "Email",
      filter: {},
    },
    {
      id: "phone_number",
      header: "Phone Number",
      filter: {},
    },
    {
      id: "merchant_user_type",
      header: "Merchant User Type",
      filter: {},
    },
    {
      id: "created_at",
      header: "Created On",
      filter: {},
    },
    {
      id: "actions",
      header: "Actions",
    },
  ];
  const tableData = (data: any) => [
    ...data?.map((item: any) => {
      return {
        ...item,
        first_name: beautify(item?.first_name),
        last_name: beautify(item?.last_name),
        parent_merchant: item?.parent_merchant?.business_name || "N/A",
        merchant_user_type: item?.merchant_user_type.replace("MERCHANT_", ""),
        created_at: formatDate(item?.created_at),

        actions: (
          <FormActions
            actions={[
              createViewAction(url, item?.id),
              ...(role === "Merchant" ? [createEditAction(url, item?.id)] : []),
            ]}
          />
        ),
      };
    }),
  ];

  const viewData = (item: any) => {
    const data = item?.results || item;
    return {
      sections: [
        {
          title: "Merchant Users Details",
          fields: {
            id: data?.id,
            first_name: data?.first_name,
            last_name: data?.last_name,
            email: data?.email,
            phone_number: data?.phone_number,
            merchant_user_type: data?.merchant_user_type,
            parent_merchant: data?.parent_merchant?.business_name,
            parent_merchant_number: data?.parent_merchant?.merchant_number,
            parent_merchant_email: data?.parent_merchant?.email,
            parent_merchant_phone: data?.parent_merchant?.phone_number,
            user_status: <StatusTags status={data?.user_status} />,
            created_on: formatDate(data?.created_at),
          },
        },
      ],
    };
  };

  // const formRoute = role === "Merchant" || role === "Admin" ? getForm(url) : "";
  const formRoute = getForm(url);

  const tableTitle = "Merchant Users";

  return { tableData, tableColumns, formRoute, viewData, tableTitle };
};

// FORM
export const useMerchantUserForm = ({ form }: TFormHookParams) => {
  const { loadData, data: merchants, loading } = useGet();

  const permissions = useGetUserPermissions();
  const [merchantLists, setMerchantLists] = useState<any[]>([]);
  const gottenPermissions = useRef(false);

  useEffect(() => {
    loadData({
      api: API_USERS,
      params: {
        role_name: "merchant",
        // is_parent: true,
      },
    });
    gottenPermissions.current = true;
  }, []);

  useEffect(() => {
    if (merchants?.results) {
      setMerchantLists((prev) => {
        const combined = [...prev, ...merchants.results];
        return [...new Map(combined.map((p) => [p.username, p])).values()];
      });

      if (gottenPermissions.current && merchants?.next) {
        let nextUrl = merchants.next
          .replace(/^https?:\/\/[^/]+/, "")
          .replace(/^\/api/, "")
          .replace(/^\/+/, "")
          .replace(/\/{2,}/g, "/");
        loadData({ api: nextUrl });
      }
    }
  }, [merchants]);

  const filteredMerchants = merchantLists?.filter(
    (user) => user.first_name?.trim() !== "" || user.last_name?.trim() !== "",
  );

  const sections: IFormSection[] = [
    {
      title: "Merchant User Details",
      fields: [
        {
          field_name: "merchant_id",
          type: "select",
          label: "Parent Merchant",
          loading: loading,
          description:
            "Select the parent merchant under which this sub-user will operate. Only users with admin permissions can assign a parent merchant.",
          props: {
            required: itemExists(permissions, [CAN_ADMIN]),
            value: form.merchant_id,
          },
          visible: itemExists(permissions, [CAN_ADMIN]),
          options_data: filteredMerchants?.map((item) => ({
            id: item?.id,
            name: `${item?.first_name} ${item?.last_name} - ${item?.user_number}`,
          })),
        },
        {
          field_name: "first_name",
          type: "text",
          label: "First Name",
          description: "Enter the first name of the merchant user.",
          props: { required: true },
          validation: VALIDATION_PRESETS.FIRST_NAME,
        },
        {
          field_name: "last_name",
          type: "text",
          label: "Last Name",
          description: "Enter the last name of the merchant user.",
          props: { required: true },
          validation: VALIDATION_PRESETS.LAST_NAME,
        },
        {
          field_name: "email",
          type: "email",
          label: "Email",
          description:
            "Enter a valid email address for the merchant user. This will be used for login and notifications.",
          props: { required: true },
          // validation: VALIDATION_PRESETS.EMAIL,
        },
        {
          field_name: "phone_number",
          type: "tel",
          label: "Phone Number",
          description:
            "Enter a valid phone number for the merchant user. Ensure it is active for contact and verification purposes.",
          props: { required: true },
          validation: VALIDATION_PRESETS.PHONE_UG,
        },
        {
          field_name: "merchant_user_type",
          type: "select",
          label: "Merchant User Type",
          description:
            "Select the role of the merchant user, such as Admin, Staff, Accountant, or Agent, to define their access and permissions.",
          props: {
            required: true,
            placeholder: "Select Merchant User Type",
          },
          options_data: [
            { id: `MERCHANT_${ADMIN}`, name: ADMIN },
            { id: `MERCHANT_${STAFF}`, name: STAFF },
            { id: `MERCHANT_${ACCOUNTANT}`, name: ACCOUNTANT },
            { id: `MERCHANT_${AGENT}`, name: AGENT },
          ],
        },
      ],
    },
  ];

  const redirect = getRegister(url);
  // SUBMIT
  function onSubmit(payload: any, id: string | number) {
    let params = { ...payload };

    return {
      id,
      api: API_MERCHANT_USERS,
      params,
    };
  }

  return { sections, redirect, onSubmit };
};
